// src/lib/auth-client.ts
'use client'

export function getAuthToken(): string | null {
    if (typeof window === 'undefined') return null

    // Prefer cookie if you later set it; fall back to localStorage for now
    const cookieMatch = document.cookie.match(/(?:^|;\s*)airies_token=([^;]+)/)
    if (cookieMatch) return decodeURIComponent(cookieMatch[1])

    return localStorage.getItem('airies_token')
}

/**
 * Sets the authentication token in localStorage and optionally in a cookie
 */
export function setAuthToken(token: string, rememberMe: boolean = true): void {
    if (typeof window === 'undefined') return

    // Store in localStorage
    localStorage.setItem('airies_token', token)

    // Optionally set cookie (for remember me functionality)
    if (rememberMe) {
        const expires = new Date()
        expires.setFullYear(expires.getFullYear() + 1) // 1 year expiry
        document.cookie = `airies_token=${encodeURIComponent(token)}; Path=/; Expires=${expires.toUTCString()}; SameSite=Lax`
    }
}

/**
 * Persists authentication data (token and user info) to localStorage and cookie
 * Similar to persistAuth in SSO page but reusable
 */
export function persistAuthData(token: string, user: any, rememberMe: boolean = true): void {
    if (typeof window === 'undefined') return

    // Store token
    localStorage.setItem('airies_token', token)
    
    // Store user data
    if (user) {
        localStorage.setItem('airies_user', JSON.stringify(user))
    }

    // Set cookie
    const days = rememberMe ? 7 : 1
    const expires = new Date()
    expires.setDate(expires.getDate() + days)
    document.cookie = `airies_token=${token}; Path=/; Expires=${expires.toUTCString()}; SameSite=Lax`
}

/**
 * Clears all authentication data (localStorage and cookies)
 */
export function clearAuth(): void {
    if (typeof window === 'undefined') return

    // Clear localStorage (keep airies_device_id - device ID must remain stable)
    localStorage.removeItem('airies_token')
    localStorage.removeItem('airies_user')

    // Clear cookie
    document.cookie = 'airies_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
}

/**
 * Checks if a response indicates a forbidden/unauthorized error (403 or "Forbidden resource" message)
 * and handles logout/redirect if needed.
 * 
 * @param response - The fetch Response object
 * @param router - Next.js router instance (optional, will use window.location if not provided)
 * @returns true if the error was handled (user should stop processing), false otherwise
 */
export async function handleAuthError(
    response: Response,
    router?: { replace: (path: string) => void }
): Promise<boolean> {
    // 401: redirect to login (with reason for SESSION_REVOKED from device removal)
    if (response.status === 401) {
        clearAuth()
        let redirectPath = '/login'
        try {
            const body = await response.clone().json().catch(() => null)
            if (body?.errorCode === 'SESSION_REVOKED') {
                redirectPath = '/login?reason=session_revoked'
            }
        } catch {
            /* ignore */
        }
        if (typeof window !== 'undefined') {
            window.location.href = redirectPath
        }
        return true
    }
    // 403: show Access denied, redirect to login
    if (response.status === 403) {
        clearAuth()
        const { toast } = await import('sonner')
        toast.error('Access denied')
        if (typeof window !== 'undefined') {
            window.location.href = '/login'
        }
        return true
    }

    // Check for "Forbidden resource" message in response body
    try {
        const body = await response.clone().json().catch(() => null)
        const errorMessage = typeof body === 'string' 
            ? body 
            : body?.message || body?.error || ''

        if (
            typeof errorMessage === 'string' && 
            errorMessage.toLowerCase().includes('forbidden resource')
        ) {
            await handleSessionExpired(router)
            return true
        }
    } catch {
        // If we can't parse the body, just check status (already done above)
    }

    return false
}

/**
 * Handles session revoked (e.g. from device removal): clears auth and redirects to login.
 */
export function handleSessionRevoked(navigate?: (path: string) => void): void {
    clearAuth()
    const path = '/login?reason=session_revoked'
    if (navigate) {
        navigate(path)
    } else if (typeof window !== 'undefined') {
        window.location.href = path
    }
}

/**
 * Handles session expiration: clears auth, shows toast, and redirects to login
 */
async function handleSessionExpired(
    router?: { replace: (path: string) => void }
): Promise<void> {
    // Clear auth data
    clearAuth()

    // Show toast notification (dynamic import to avoid SSR issues)
    const { toast } = await import('sonner')
    toast.error('You are logged out or session expired', {
        description: 'Please log in again to continue.',
        duration: 5000,
    })

    // Redirect to login page
    if (router) {
        router.replace('/login')
    } else if (typeof window !== 'undefined') {
        window.location.href = '/login'
    }
}